import { AbilityType } from '../types.js';
import { isVerb, isTarget, isGroupTarget, type Verb, type Target } from '../shared/targeting.js';

// ── Voice: who "you" / "opponent" refer to in rule text ──────────────
//
// Effect text is written relative to the card's OWNER. The owner is
// "you" and their opponent is "opponent". Most surfaces (card display)
// keep that second-person voice. The game log, read by the human,
// wants neutral third-person labels and flips them when the AI owns
// the card. A `Voice` carries the four role words so the templates
// interpolate the right ones instead of hardcoding "you"/"opponent"
// and then post-processing the string.

export interface Voice {
  you: string;    // owner as subject     ("you" | "player" | "opponent")
  your: string;   // owner possessive     ("your" | "player's" | "opponent's")
  foe: string;    // owner's opponent     ("opponent" | "player")
  foes: string;   // opponent possessive  ("opponent's" | "player's")
}

/** Second-person voice for card display / tooltips. */
const DISPLAY_VOICE: Voice = { you: 'you', your: 'your', foe: 'opponent', foes: "opponent's" };

/** Third-person voice for the game log, keyed by which side owns the
 *  card. The log is read by the human ('player'), so when the AI
 *  ('opponent') owns it, "you" becomes "opponent" and "opponent"
 *  becomes "player". */
function logVoice(ownerSide: 'player' | 'opponent'): Voice {
  return ownerSide === 'player'
    ? { you: 'player', your: "player's", foe: 'opponent', foes: "opponent's" }
    : { you: 'opponent', your: "opponent's", foe: 'player', foes: "player's" };
}

// ── Verb + Target templated rule text ────────────────────────────────

const TARGET_PHRASE: Record<Target, (v: Voice) => string> = {
  'self': () => 'this character',
  'weakest-ally': v => `${v.your} weakest ally`,
  'strongest-ally': v => `${v.your} strongest ally`,
  'all-allies': v => `all ${v.your} characters`,
  'all-other-allies': v => `${v.your} other characters`,
  'weakest-enemy': v => `${v.foes} weakest character`,
  'strongest-enemy': v => `${v.foes} strongest character`,
  'all-enemies': v => `all ${v.foe} characters`,
  'all-characters': () => 'all characters',
  'strongest-character': () => 'the strongest character (of either player)',
  'weakest-character': () => 'the weakest character (of either player)',
  'self-and-weakest-enemy': v => `this character and ${v.foes} weakest character`,
  'self-and-strongest-enemy': v => `this character and ${v.foes} strongest character`,
};

// Per-verb override: when a verb's template embeds singular-agreement words
// like "its" or "it", a plural target phrase ("all X") disagrees with the
// singular pronoun. `return` says "to its owner's hand", so for group
// targets we substitute an "each X" phrasing that's grammatically singular.
// Singular targets fall through to TARGET_PHRASE since they already agree.
const RETURN_TARGET_PHRASE: Partial<Record<Target, (v: Voice) => string>> = {
  'all-characters':   () => 'each character',
  'all-enemies':      v => `each ${v.foe} character`,
  'all-allies':       v => `each of ${v.your} characters`,
  'all-other-allies': v => `each of ${v.your} other characters`,
};

// Per-verb override for `silence`. Two purposes:
//   1. Group targets get an "each X" phrasing ("Remove ability from
//      each opponent character" vs the awkward "all opponent characters").
//   2. Singular-target picks (strongest-enemy, etc.) get a "with an
//      ability" suffix to surface the candidate-pool filter — see
//      `isEligible` in Targeting.ts, which skips vanilla characters,
//      already-silenced ones, and characters whose only ability is
//      on-play (since on-play has already fired by the time silence
//      lands). The suffix isn't applied to `all` targets because
//      those don't make a selection — the filter just trims who is
//      hit, and "each X" already reads as inclusive.
const SILENCE_TARGET_PHRASE: Partial<Record<Target, (v: Voice) => string>> = {
  'all-characters':           () => 'each character',
  'all-enemies':              v => `each ${v.foe} character`,
  'all-allies':               v => `each of ${v.your} characters`,
  'all-other-allies':         v => `each of ${v.your} other characters`,
  'weakest-ally':             v => `${v.your} weakest ally with an ability`,
  'strongest-ally':           v => `${v.your} strongest ally with an ability`,
  'weakest-enemy':            v => `${v.foes} weakest character with an ability`,
  'strongest-enemy':          v => `${v.foes} strongest character with an ability`,
  'strongest-character':      () => 'the strongest character with an ability (of either player)',
  'weakest-character':        () => 'the weakest character with an ability (of either player)',
  'self-and-weakest-enemy':   v => `this character and ${v.foes} weakest character with an ability`,
  'self-and-strongest-enemy': v => `this character and ${v.foes} strongest character with an ability`,
};

// Per-verb override for `exhaust`. Target selection filters out already-
// exhausted characters (see `isEligible` in Targeting.ts), so the rules
// text should say "ready" to surface that filter for the player —
// otherwise "Exhaust opponent's strongest character" reads as targeting
// the strongest regardless of state, even though the engine actually
// picks the strongest READY one. Each phrase places "ready" before the
// noun that gets selected.
//
// `self` falls through to TARGET_PHRASE — "this character" already reads
// as "the speaker", and "this ready character" is awkward. In every
// trigger where exhaust+self lands at runtime, the source character is
// effectively ready at that moment (just played, or about to activate).
//
// The self-and-X compounds attach "ready" only to the X side. The "this
// character" half isn't filtered (engine still tries to exhaust self,
// no-ops if already exhausted), so leaving it unqualified matches the
// behavior.
const EXHAUST_TARGET_PHRASE: Partial<Record<Target, (v: Voice) => string>> = {
  'weakest-ally':             v => `${v.your} weakest ready ally`,
  'strongest-ally':           v => `${v.your} strongest ready ally`,
  'all-allies':               v => `all ${v.your} ready characters`,
  'all-other-allies':         v => `${v.your} other ready characters`,
  'weakest-enemy':            v => `${v.foes} weakest ready character`,
  'strongest-enemy':          v => `${v.foes} strongest ready character`,
  'all-enemies':              v => `all ready ${v.foe} characters`,
  'all-characters':           () => 'all ready characters',
  'strongest-character':      () => 'the strongest ready character (of either player)',
  'weakest-character':        () => 'the weakest ready character (of either player)',
  'self-and-weakest-enemy':   v => `this character and ${v.foes} weakest ready character`,
  'self-and-strongest-enemy': v => `this character and ${v.foes} strongest ready character`,
};

// Mirror of EXHAUST_TARGET_PHRASE for `keep-exhausted`. Targeting filters
// the candidate pool to ALREADY-exhausted characters (see `isEligible` in
// Targeting.ts), so the rules text surfaces "exhausted" in the noun phrase
// — otherwise "the strongest character" reads as picking the strongest
// regardless of state, even though the engine only locks an already-
// exhausted one. The doubled "exhausted" (target phrase + verb suffix
// "exhausted through their next turn") is the price of accurately
// communicating both the filter and the lock effect.
const KEEP_EXHAUSTED_TARGET_PHRASE: Partial<Record<Target, (v: Voice) => string>> = {
  'weakest-ally':             v => `${v.your} weakest exhausted ally`,
  'strongest-ally':           v => `${v.your} strongest exhausted ally`,
  'all-allies':               v => `all ${v.your} exhausted characters`,
  'all-other-allies':         v => `${v.your} other exhausted characters`,
  'weakest-enemy':            v => `${v.foes} weakest exhausted character`,
  'strongest-enemy':          v => `${v.foes} strongest exhausted character`,
  'all-enemies':              v => `all exhausted ${v.foe} characters`,
  'all-characters':           () => 'all exhausted characters',
  'strongest-character':      () => 'the strongest exhausted character (of either player)',
  'weakest-character':        () => 'the weakest exhausted character (of either player)',
  'self-and-weakest-enemy':   v => `this character and ${v.foes} weakest exhausted character`,
  'self-and-strongest-enemy': v => `this character and ${v.foes} strongest exhausted character`,
};

function renderVerb(verb: Verb, target: Target, intensity: number, v: Voice): string {
  const t = TARGET_PHRASE[target](v);
  switch (verb) {
    case 'damage':
      return `Deal ${intensity} damage to ${t}`;
    case 'boost':
      return intensity >= 0
        ? `+${intensity} Power to ${t}`
        : `${intensity} Power to ${t}`;   // negative sign included
    case 'armor':
      return `+${intensity} Armor to ${t}`;
    case 'exhaust': {
      const phrase = (EXHAUST_TARGET_PHRASE[target] ?? TARGET_PHRASE[target])(v);
      return `Exhaust ${phrase}`;
    }
    case 'keep-exhausted': {
      const phrase = (KEEP_EXHAUSTED_TARGET_PHRASE[target] ?? TARGET_PHRASE[target])(v);
      // Imperative voice matches the other verbs ("Exhaust X", "Destroy
      // X"). "Lock" reads as the closest game-action verb for a stasis
      // / freeze effect; the "until their next turn" suffix conveys
      // duration.
      return `Lock ${phrase} until their next turn`;
    }
    case 'ready':
      return `Ready ${t}`;
    case 'return': {
      const phrase = (RETURN_TARGET_PHRASE[target] ?? TARGET_PHRASE[target])(v);
      return `Return ${phrase} to its owner's hand`;
    }
    case 'destroy':
      return `Destroy ${t}`;
    case 'slay':
      return isGroupTarget(target)
        ? `Destroy ${t} with ${intensity}+ Power`
        : `Destroy ${t} if it has ${intensity}+ Power`;
    case 'silence': {
      const phrase = (SILENCE_TARGET_PHRASE[target] ?? TARGET_PHRASE[target])(v);
      return `Remove the ability from ${phrase}`;
    }
  }
}

export function getEffectDescription(
  effect: string,
  intensity: number,
  abilityType: AbilityType | 'action',
  target?: Target,
): string {
  const TRIGGER_PREFIXES: Record<AbilityType | 'action', string> = {
    'on-play': 'On play: ',
    'passive': 'Passive: ',
    'activated': 'Activate: ',
    'on-attack': 'When this character attacks: ',
    'on-attack-alone': 'When this character attacks alone: ',
    'on-damage-opponent': 'When this character damages opponent: ',
    'end-of-turn': 'End of turn: ',
    'beginning-of-turn': 'Beginning of turn: ',
    'on-play-character': 'When you play another character: ',
    'on-play-action': 'When you play an action card: ',
    'action': '',
  };
  return TRIGGER_PREFIXES[abilityType] + getEffectBody(effect, intensity, target, DISPLAY_VOICE);
}

/** The effect sentence WITHOUT the trigger prefix ("On play: " etc.),
 *  rendered in the given `voice`. Split out from getEffectDescription so
 *  callers that supply their own lead-in (e.g. the game log, which
 *  prints "<CardName>: <body>") can reuse the exact rule text in a
 *  different voice. getEffectDescription is just this in DISPLAY_VOICE
 *  plus the per-trigger prefix. */
export function getEffectBody(effect: string, intensity: number, target: Target | undefined, v: Voice): string {
  // Verb+target path: templated text.
  if (target && isVerb(effect) && isTarget(target)) {
    return `${renderVerb(effect, target, intensity, v)}.`;
  }

  // Legacy atomic-slug path: player-surface, compound, conditional, passives.
  switch (effect) {
    case 'draw': return `Draw ${intensity === 1 ? 'a card' : `${intensity} cards`}.`;
    case 'draw-and-spirit': return `Draw ${intensity === 1 ? 'a card' : `${intensity} cards`} and gain ${intensity} Spirit.`;
    case 'damage-opponent': return `Deal ${intensity} direct damage to ${v.foe}.`;
    case 'heal': return `Restore ${intensity} HP.`;
    case 'spirit-gain': return `Gain ${intensity} Spirit.`;
    case 'steal-spirit': return `Steal up to ${intensity} Spirit from ${v.foe}.`;
    case 'return-exchange': return `Return ${v.your} weakest ally to ${v.your} hand. If so, return ${v.foes} strongest character to ${v.foes} hand.`;
    case 'drain-spirit': return `Reduce ${v.foes} Spirit by ${intensity}.`;
    case 'copy-power': return `Set this character's Power equal to the strongest enemy's Power.`;
    case 'steal-power': return `KO ${v.your} weakest ally and add their Power to this character.`;
    case 'sacrifice-heal': return `KO ${v.your} weakest ally. Restore ${intensity} HP.`;
    case 'discard-enemy-hand': return `${capitalize(v.foe)} discards ${intensity === 1 ? 'a random card' : `${intensity} random cards`} from their hand.`;
    case 'mill-enemy': return `Discard the top ${intensity === 1 ? 'card' : `${intensity} cards`} of ${v.foes} deck.`;
    case 'recycle-hand': return `Shuffle up to ${intensity} cards from ${v.your} discard pile into ${v.your} deck, then draw 1.`;
    case 'shuffle-hand': return `Discard ${v.your} hand, then draw that many cards plus 1.`;
    case 'equalize': return `Set all characters in both rings to ${intensity} Power.`;
    case 'advance-clock': return `Advance the play-clock by ${intensity}.`;
    case 'rewind-clock': return `Rewind the play-clock by ${intensity}.`;
    case 'passive-attack-ready': return `This character does not become exhausted after attacking.`;
    case 'passive-damage-exhausted': return `At end of turn, all exhausted enemies take ${intensity} damage.`;
    case 'grant-extra-rally': return `Play ${intensity} additional character${intensity > 1 ? 's' : ''} this turn.`;
    case 'grant-extra-support': return `Support ${intensity} additional time${intensity > 1 ? 's' : ''} this turn.`;
    case 'passive-boost-allies': return `Allies gain +${intensity} Power.`;
    case 'passive-intimidate': return `All ${v.foe} characters get -${intensity} Power.`;
    case 'passive-support-bonus': return `Gain an additional +${intensity} Power when ${v.you} support.`;
    case 'passive-extra-support': return `${capitalize(v.you)} can support ${intensity} additional time${intensity > 1 ? 's' : ''} each turn.`;
    case 'passive-easy-support': return `${capitalize(v.your)} characters can support with only 1 shared tag.`;
    case 'passive-hand-size': return `${capitalize(v.your)} maximum hand size is ${intensity > 0 ? 'increased' : 'decreased'} by ${Math.abs(intensity)}.`;
    case 'passive-pierce': return `When this character is blocked, excess damage spills over to the ${v.foe}.`;
    case 'passive-leech': return `When this character deals damage to the ${v.foe}, ${v.you} gain that much HP.`;
    case 'passive-cannot-block': return `This character cannot block.`;
    case 'passive-disable-activated': return `Characters can't use activated (↩️) or combat (⚔️) abilities.`;
    case 'passive-disable-on-play': return `On-play (⚡ and 💫) abilities do not trigger.`;
    case 'passive-rebirth': return `When this character dies, it returns with ${intensity} power and no ability.`;
    default: return effect;
  }
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

/** Render an effect body for the game log in neutral third-person voice
 *  ("player" / "opponent"), correct for whichever side owns the card.
 *  The log is read by the human ('player'), so an AI-owned card has its
 *  "you"/"opponent" references flipped (see logVoice). Used to print
 *  on-play ability lines like "Montezuma: Deal 300 direct damage to
 *  player." */
export function describeEffectForLog(
  effect: string,
  intensity: number,
  target: Target | undefined,
  ownerSide: 'player' | 'opponent',
): string {
  return getEffectBody(effect, intensity, target, logVoice(ownerSide));
}

/** Get description for a character ability */
export function getAbilityDescription(ability: {
  type: AbilityType;
  effect: string;
  target?: Target;
  intensity?: number;
}): string {
  return getEffectDescription(ability.effect, ability.intensity ?? 0, ability.type, ability.target);
}

/** Get description for an action card */
export function getActionDescription(def: {
  effect: string;
  target?: Target;
  intensity?: number;
}): string {
  return getEffectDescription(def.effect, def.intensity ?? 0, 'action', def.target);
}
